import React, { Fragment, useEffect, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Popover, Button, Checkbox, Tabs, DatePicker, Select,
  Badge, Drawer, notification,
} from 'antd';
import {
  LoadingOutlined, EditOutlined, UserOutlined, CaretRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill';
import { v4 as uuidv4 } from 'uuid';
import H3Text from '@Uilib/h3Text';
import Constants, { toISTDate } from '@Apis/constants';
import Helpers from '@Apis/helpers';
import H3FormInput from '@Uilib/h3FormInput';
import RfqActions from '@Actions/purchase/rfq/rfqActions';
import Timer from '@Components/Common/Timer';
import Attachment from '@Components/Common/Attachment';
import AttachmentActions from '@Actions/attachmentActions';
import AnalyticsActions from '@Actions/application/analyticsActions';
import Invitees from './Invitees';
import Participants from './Participants';
import RfqLines from './RfqLines';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faPrint } from '@fortawesome/free-solid-svg-icons';
import './style.scss';
import H3Image from '@Uilib/h3Image';
import closeIcon from '@Images/icons/icon-close-blue.png';
import CurrenciesForm from '@Components/Admin/AdminConfiguration/Currencies/CurrenciesForm';
import RfqPurchaseOrders from '@Components/Purchase/Rfq/RfqPurchaseOrders';
import Leaderboard from './RfqLeaderboard';
import HideComponent from '../../../Common/RestrictedAccess/HideComponent';
import ViewLoadingSkull from '../../../Common/ViewLoadingSkull';
import PRZSelect from '../../../Common/UI/PRZSelect';

const { TabPane } = Tabs;

const ViewRfq = ({
  user, match, getRfqById, getRfqByIdLoading, updateRfqParticipant, updateRfqParticipantLoading, updateRfqStatus, updateRfqStatusLoading,
  history, updateRfqConfig, updateRfqConfigLoading, downloadDocument, updateAttachmentLoading, updateAttachment, getAttachmentById, priceMasking, isQuickView,
}) => {
  const [currentTab, setCurrentTab] = useState('/rfq-lines');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [data, setData] = useState([]);
  const [rfqLineData, setRfqLineData] = useState([]);
  const [call, setCall] = useState(false);
  const [selectedRfq, setSelectedRfq] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [isGetAttachment, setIsGetAttachment] = useState(false);
  const [cancellationPop, setCancellationPop] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [endTime, setEndTime] = useState('');
  const [showRfqPoDrawer, setShowRfqPoDrawer] = useState('');
  const [isCallbackApiCalled, setIsCallbackApiCalled] = useState(false);
  const now = dayjs(selectedRfq?.end_time);

  const hasUpdatePermission = Helpers.getPermission(Helpers.permissionEntities.REQUEST_FOR_QUOTATION, Helpers.permissionTypes.UPDATE, user);

  useEffect(() => {
    getRfqById(user?.tenant_info?.org_id, Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.REQUEST_FOR_QUOTATION, Helpers.permissionTypes.READ)?.join(','), match.params.rfqId, (rfqData) => {
      setSelectedRfq(rfqData);
      setEndTime(dayjs(rfqData?.end_time));
      setCurrentTab(Helpers.getValueFromUrl('tab') || '/rfq-lines');
    }, isCallbackApiCalled);
  }, [call]);

  useEffect(() => {
    if (selectedRfq?.rfq_id) {
      const participantsData = selectedRfq?.rfq_participants?.map((item) => ({
        ...item, key: uuidv4(), dial_code: String(item?.dial_code), seller_name: item?.participant_name,
      }));
      setData(selectedRfq?.rfq_participants ? participantsData : []);
      setRfqLineData(selectedRfq?.rfq_lines);
    }
  }, [selectedRfq]);

  const addNewRow = () => {
    const copyData = JSON.parse(JSON.stringify(data));
    copyData.push({
      key: uuidv4(),
      seller_id: '',
      seller_name: '',
      dial_code: Helpers.getDialCode(user?.tenant_info?.country_code),
      phone_number: '',
      email: '',
      phId: '',
      sellerId: '',
      emaiId: '',
    });
    setData(copyData);
  };

  const handleSubmit = (action, record) => {
    setFormSubmitted(true);
    if (action === 'ADD') {
      const payload = data?.filter((item) => !item?.rfq_participant_id)?.map((item) => (
        {
          action,
          rfq_id: selectedRfq?.rfq_id,
          seller_id: item?.seller_id,
          tenant_id: selectedRfq?.tenant_id,
          participant_name: item?.seller_name,
          dial_code: item?.dial_code,
          email: item?.email,
          phone_number: item?.phone_number,
        }
      ));
      updateRfqParticipant(payload, () => {
        setCall(!call);
      });
    } else {
      const payload = [{
        action,
        rfq_id: record?.rfq_id,
        rfq_participant_id: record?.rfq_participant_id,
        tenant_id: record?.tenant_id,
        seller_id: record?.seller_id,
        participant_name: record?.seller_name,
        dial_code: record?.dial_code,
        email: record?.email,
        phone_number: record?.phone_number,
      }];
      updateRfqParticipant(payload, () => {
        setCall(!call);
        setIsEditing(false);
      });
    }
  };

  const handleDelete = (key) => {
    if (data?.length === 1) {
      setData([
        {
          key: uuidv4(),
          seller_id: '',
          seller_name: '',
          dial_code: Helpers.getDialCode(user?.tenant_info?.country_code),
          phone_number: '',
          email: '',
        },
      ]);
    } else {
      const copyData = data.filter((item) => item.key !== key);
      setData(copyData);
    }
  };

  const handleFileChange = (fileListData) => {
    const copyFileList = fileListData?.fileList?.map((item) => ({
      ...item,
      url: item?.response?.response?.location || item?.url,
    }));

    const attachments = copyFileList?.map((attachment) => ({
      url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
    }));

    if (fileListData?.file.status === 'done') {
      const payload = {
        entity_id: selectedRfq?.rfq_id,
        entity_name: 'sales_order',
        attachments,
      };
      updateAttachment(payload, () => {
        getAttachmentById(selectedRfq?.rfq_id, 'rfq', () => {
          setIsGetAttachment(true);
        });
      });
    }
    if (fileListData?.file.status === 'removed') {
      const payload = {
        entity_id: selectedRfq?.rfq_id,
        entity_name: 'rfq',
        attachments,
      };
      updateAttachment(payload, () => {
        getAttachmentById(selectedRfq?.rfq_id, 'rfq', () => {
          setIsGetAttachment(true);
        });
      });
    }

    setFileList(attachments.length > 0 ? attachments : []);
  };

  const currentDate = dayjs();
  const currentHour = currentDate.hour();
  const currentMinute = currentDate.minute();

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;

  const fixedMenuBar = user?.side_menu_bar_type === 'FIXED';

  return (
    <Fragment>
      <div className={`view-document__wrapper view-rfq__wrapper ${!isQuickView ? 'view-document__wrapper-page' : ''}`}>
        {(!getRfqByIdLoading && selectedRfq?.rfq_id)
          ? (
            <div className="ant-row">
              <div className="ant-col-md-18 ant-col-xs-24">
                <div className={`view-left__wrapper ${!isQuickView ? 'is-page-view' : ''}`}>
                  <div className="view-document__title">
                    <div className="view-document__title-number-wrapper">
                      <H3Text text={`${selectedRfq?.rfq_number} - ${selectedRfq?.description}`} className="view-document__title-number" />
                      <div className="view-document__title-status" style={{ backgroundColor: Helpers.getStatusColor(selectedRfq?.status).color }}>
                        {selectedRfq?.status?.replaceAll('_', ' ')}
                      </div>
                      {/* {
                        dayjs(selectedRfq?.po_expiry_date).format('YYYY-MM-DD') < now.format('YYYY-MM-DD') && (
                          <div className="view-po__status" style={{ marginLeft: '5px' }}>
                            <Tag icon={<ExclamationCircleOutlined />} color="error">
                              Expired
                            </Tag>
                          </div>
                        )
                      } */}
                      {selectedRfq?.closing_status === 'CLOSED' && (
                        <div className="view-po-short-closed-tag">
                          Closed
                        </div>
                      )}
                    </div>

                    <div className="view-document__title-actions">
                      {(selectedRfq?.status === 'IN_PROGRESS') && <Timer startTime={dayjs(selectedRfq?.begin_time).add(5, 'hour').add(30, 'minute')} endTime={dayjs(selectedRfq?.end_time).add(5, 'hour').add(30, 'minute')} fullText />}

                      {selectedRfq?.status === 'DRAFT' && Helpers.getPermission(Helpers.permissionEntities.REQUEST_FOR_QUOTATION, Helpers.permissionTypes.UPDATE, user) && (
                        <div className="action-buttons">
                          <H3Text
                            text={(
                              <div
                                onClick={() => {
                                  history.push(`/purchase/rfq/update/${selectedRfq?.rfq_id}`);
                                }}
                              >
                                <EditOutlined />
                              </div>
                            )}
                            className="action-button"
                          />
                        </div>
                      )}

                      {selectedRfq?.status !== 'DRAFT' && selectedRfq?.rfq_participants?.length > 0 && (
                        <div className="action-buttons">
                          <H3Text
                            text={
                              <FontAwesomeIcon icon={faPrint} style={{ fontSize: '16px', width: '35px', textAlign: 'center' }} />
                            }
                            onClick={() => downloadDocument({
                              url: `${Constants.RFQ}/comparator/download?rfq_id=${selectedRfq?.rfq_id}`,
                              document_type: 'Quotation Comparator',
                              document_number: selectedRfq?.rfq_number,
                              key: uuidv4(),
                            })}
                            className="action-button"
                          />
                        </div>
                      )}
                      <div className="action-buttons">

                        {selectedRfq?.status === 'COMPLETED'
                          && (
                            <H3Text
                              text={(
                                <div style={{ width: '120px', textAlign: 'center' }}>
                                  + Purchase Orders
                                </div>
                              )}
                              onClick={() => {
                                if (selectedRfq?.negotiations_by_rank?.filter((item) => item?.rfq_status === 'ACCEPTED')?.length) {
                                  setShowRfqPoDrawer(true);
                                } else {
                                  notification.open({
                                    message: 'Please shortlist at least one quotation to create purchase orders',
                                    duration: 4,
                                    placement: 'top',
                                    type: 'error',
                                  });
                                }
                              }}
                              className="action-button action-button-big"
                            />
                          )}
                        {!['VOID', 'COMPLETED'].includes(selectedRfq?.status) && Helpers.getPermission(Helpers.permissionEntities.REQUEST_FOR_QUOTATION,

                          Helpers.permissionTypes.UPDATE, user) && (
                            <Popover
                              placement="topRight"
                              title="Are you sure you want to cancel this RFQ?"
                              content={(
                                <React.Fragment>
                                  <H3FormInput
                                    name="cancellation reason"
                                    type="text"
                                    containerClassName="orgInputContainer"
                                    labelClassName="orgFormLabel"
                                    inputClassName="orgFormInput"
                                    placeholder=""
                                    onChange={(event) => setCancellationReason(event.target.value)}
                                    value={cancellationReason}
                                    label="Cancellation reason"
                                    maxlength="100"
                                    required
                                    showError={currentStatus === 'VOID' && !cancellationReason}
                                  />
                                  <Button
                                    onClick={() => {
                                      if (selectedRfq?.reference_po?.length > 0) {
                                        notification.open({
                                          placement: 'top',
                                          type: 'error',
                                          duration: 4,
                                          message: 'You have open purchase orders linked to this RFQ. Please delete the purchase orders first.',
                                        });
                                      } else {
                                        setCurrentStatus('VOID');
                                        if (cancellationReason) {
                                          updateRfqStatus({
                                            status: 'VOID',
                                            rfq_id: selectedRfq?.rfq_id,
                                            reason_for_cancellation: cancellationReason,
                                          }, () => {
                                            setCall(!call);
                                            setCurrentStatus('');
                                            setCancellationReason('');
                                          });
                                          setCancellationPop(false);
                                        }
                                      }
                                    }}
                                    type="primary"
                                    size="small"
                                    loading={updateRfqStatusLoading}
                                  >
                                    Submit
                                  </Button>
                                </React.Fragment>
                              )}
                              trigger="click"
                            >
                              <H3Text
                                text={(
                                  <div style={{ width: '50px', textAlign: 'center' }}>
                                    {(updateRfqStatusLoading && currentStatus === 'VOID')
                                      ? <LoadingOutlined /> : (
                                        <span>Cancel</span>
                                      )}
                                  </div>
                                )}
                                onClick={() => {
                                  setCancellationPop(false);
                                }}
                                className="action-button action-button-big"
                              />
                            </Popover>
                          )}
                        {selectedRfq?.status === 'IN_PROGRESS' && Helpers.getPermission(Helpers.permissionEntities.REQUEST_FOR_QUOTATION,
                          Helpers.permissionTypes.UPDATE, user) && (
                            <Popover
                              placement="topRight"
                              title="Are you sure you want to end this auction?"
                              content={(
                                <React.Fragment>
                                  <Button
                                    onClick={() => {
                                      updateRfqConfig({
                                        rfq_id: selectedRfq?.rfq_id,
                                        end_time: dayjs().valueOf(),
                                      }, () => {
                                        setCall(!call);
                                      });
                                      setCancellationPop(false);
                                    }}
                                    type="primary"
                                    size="small"
                                    loading={updateRfqConfigLoading}
                                  >
                                    Submit
                                  </Button>
                                </React.Fragment>
                              )}
                              trigger="click"
                            >
                              <H3Text
                                text={(
                                  <div style={{ width: '78px', textAlign: 'center' }}>
                                    {(updateRfqStatusLoading && currentStatus === 'IN_PROGRESS')
                                      ? <LoadingOutlined /> : (
                                        <span>End RFQ</span>
                                      )}
                                  </div>
                                )}
                                onClick={() => {
                                  if (selectedRfq?.status !== 'IN_PROGRESS') {
                                    setCancellationPop(false);
                                  }
                                }}
                                className="action-button action-button-big"
                              />
                            </Popover>
                          )}
                        {selectedRfq?.status === 'DRAFT' && Helpers.getPermission(Helpers.permissionEntities.REQUEST_FOR_QUOTATION,
                          Helpers.permissionTypes.UPDATE, user) && (
                            <Popover
                              placement="topRight"
                              title={selectedRfq?.rfq_participants?.length === 0 ? 'You have not selected any participants?' : 'Are you sure you want to start the RFQ?'}
                              content={(
                                <React.Fragment>
                                  <Button
                                    onClick={() => {
                                      updateRfqConfig({
                                        rfq_id: selectedRfq?.rfq_id,
                                        begin_time: dayjs().valueOf(),
                                      }, () => {
                                        setCall(!call);
                                      });
                                      setCancellationPop(false);
                                    }}
                                    style={{ marginRight: '5px' }}
                                    type="primary"
                                    size="small"
                                    loading={updateRfqConfigLoading}
                                  >
                                    Start RFQ
                                  </Button>
                                </React.Fragment>
                              )}
                              trigger="click"
                            >
                              <H3Text
                                text={(
                                  <div className={selectedRfq?.status === 'IN_PROGRESS' ? 'hover-disable' : ''} style={{ width: '78px', textAlign: 'center' }}>
                                    {(updateRfqStatusLoading && currentStatus === 'IN_PROGRESS')
                                      ? <LoadingOutlined /> : (
                                        <span>Start RFQ</span>
                                      )}
                                  </div>
                                )}
                                onClick={() => {
                                  if (selectedRfq?.status !== 'IN_PROGRESS') {
                                    setCancellationPop(false);
                                  }
                                }}
                                className="action-button action-button-big"
                              />
                            </Popover>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="document-header">
                    <div className="ant-row">
                      <div className="ant-col-md-24">
                        <H3Text
                          text={(
                            <span>
                              <UserOutlined />
                              {' '}
                              <span>{selectedRfq?.tenant_info?.tenant_name}</span>
                            </span>
                          )}
                          className="document-header__party-name"
                        />
                      </div>
                      <div className="ant-col-md-12">
                        <div className="document-header__field">
                          <H3Text text="Selection" className="document-header__field-name" />
                          <H3Text
                            text={(
                              <div className="view-rfq__config-select">
                                <PRZSelect
                                  labelInValue
                                  value={selectedRfq?.selection}
                                  onChange={(val) => {
                                    const payload = {
                                      rfq_id: selectedRfq?.rfq_id,
                                      selection: val?.value,
                                    };
                                    updateRfqConfig(payload, () => {
                                      setCall(!call);
                                    });
                                  }}
                                  bordered
                                  options={[
                                    { value: 'SINGLE_VENDOR', label: 'Single Vendor' },
                                    { value: 'MULTIPLE_VENDOR', label: 'Multiple Vendor' },
                                  ]}
                                  disabled={!['IN_PROGRESS', 'DRAFT'].includes(selectedRfq?.status) || !hasUpdatePermission}
                                />
                              </div>
                            )}
                            className="document-header__field-value"
                          />
                        </div>
                      </div>
                      <div className="ant-col-md-12">
                        <div className="document-header__field">
                          <div>
                            <Checkbox
                              checked={selectedRfq?.show_rank_to_vendor}
                              onChange={(e) => {
                                const payload = {
                                  rfq_id: selectedRfq?.rfq_id,
                                  show_rank_to_vendor: e.target.checked,
                                };
                                updateRfqConfig(payload, () => {
                                  setCall(!call);
                                });
                              }}
                              disabled={!['IN_PROGRESS', 'DRAFT'].includes(selectedRfq?.status) || !hasUpdatePermission}
                            >
                              <div className="document-header__field-name" style={{ width: '100%', fontSize: '13px' }}>
                                Show rank to participants
                              </div>
                            </Checkbox>
                          </div>
                        </div>
                      </div>
                      <div className="ant-col-md-12">
                        <div className="document-header__field">
                          <H3Text text="Strategy" className="document-header__field-name" />
                          <H3Text
                            text={(
                              <div className="view-rfq__config-select">
                                <PRZSelect
                                  labelInValue
                                  value={selectedRfq?.strategy}
                                  onChange={(val) => {
                                    const payload = {
                                      rfq_id: selectedRfq?.rfq_id,
                                      strategy: val?.value,
                                    };
                                    updateRfqConfig(payload, () => {
                                      setCall(!call);
                                    });
                                  }}
                                  bordered
                                  options={[
                                    { value: 'PRICE_BASED', label: 'Price Based' },
                                    { value: 'RANK_BASED', label: 'Rank Based' },
                                  ]}
                                  disabled={!['IN_PROGRESS', 'DRAFT'].includes(selectedRfq?.status) || !hasUpdatePermission}
                                />
                              </div>
                            )}
                            className="document-header__field-value"
                          />
                        </div>
                      </div>
                      <div className="ant-col-md-12">
                        <div className="document-header__field">
                          <Checkbox
                            checked={selectedRfq?.show_lowest_price_to_vendor}
                            onChange={(e) => {
                              const payload = {
                                rfq_id: selectedRfq?.rfq_id,
                                show_lowest_price_to_vendor: e.target.checked,
                              };
                              updateRfqConfig(payload, () => {
                                setCall(!call);
                              });
                            }}
                            disabled={!['IN_PROGRESS', 'DRAFT'].includes(selectedRfq?.status) || !hasUpdatePermission}
                          >
                            <div className="document-header__field-name" style={{ width: '100%', fontSize: '13px' }}>
                              Show lowest price to participants
                            </div>
                          </Checkbox>
                        </div>
                      </div>
                      <div className="ant-col-md-12">
                        <div className="document-header__field">
                          <H3Text text="Deadline" className="document-header__field-name" />
                          <H3Text
                            text={(
                              <div className="view-rfq__config-date">
                                <DatePicker
                                  value={endTime}
                                  onChange={(value) => {
                                    setEndTime(value);
                                    const payload = {
                                      rfq_id: selectedRfq?.rfq_id,
                                      end_time: dayjs(value).valueOf(),
                                    };
                                    updateRfqConfig(payload, () => {
                                      setCall(!call);
                                    });
                                  }}
                                  format="YYYY-MM-DD h:mm A"
                                  disabledDate={(current) => current && current < currentDate.startOf('day')}
                                  disabledTime={(current) => {
                                    const isSameDay = current && current.isSame(currentDate, 'day');
                                    if (isSameDay) {
                                      return {
                                        disabledHours: () => Array.from({ length: currentHour }, (_, index) => index),
                                        disabledMinutes: (selectedHour) => (selectedHour === currentHour ? Array.from({ length: currentMinute }, (_, index) => index) : []),
                                        disabledMeridiem: () => (currentHour < 12 ? ['PM'] : ['AM', 'PM']),
                                      };
                                    }
                                    return {};
                                  }}
                                  showTime={{
                                    defaultValue: dayjs('00:00:00', 'HH:mm:ss'),
                                    hideDisabledOptions: false,
                                  }}
                                  style={{
                                    borderRadius: '4px',
                                    height: '25px',
                                    padding: '1px 10px',
                                    background: 'white',
                                    marginTop: "-4px"
                                  }}
                                />
                                {formSubmitted && !endTime && (
                                  <div className="input-error">
                                    *Please enter end time
                                  </div>
                                )}
                              </div>
                            )}
                            className="document-header__field-value"
                          />
                        </div>
                      </div>
                      {/* {selectedRfq?.delivery_date && (
                        <div className="ant-col-md-12">
                          <div className="document-header__field">
                            <H3Text text="Delivery Date" className="document-header__field-name" />
                            <H3Text
                              text={selectedRfq?.delivery_date ? (dayjs(selectedRfq.delivery_date).format('DD/MM/YYYY')) : '-'}
                              className="document-header__field-value"
                            />
                          </div>
                        </div>
                      )} */}

                      {/* <div className="ant-col-md-12"> */}
                      {/*  <div className="document-header__field"> */}
                      {/*    <Checkbox */}
                      {/*      checked={selectedRfq?.price_with_tax} */}
                      {/*      onChange={(e) => { */}
                      {/*        const payload = { */}
                      {/*          rfq_id: selectedRfq?.rfq_id, */}
                      {/*          price_with_tax: e.target.checked, */}
                      {/*        }; */}
                      {/*        updateRfqConfig(payload, () => { */}
                      {/*          setCall(!call); */}
                      {/*        }); */}
                      {/*      }} */}
                      {/*      disabled={!['IN_PROGRESS', 'DRAFT'].includes(selectedRfq?.status)} */}
                      {/*    > */}
                      {/*      <div className="document-header__field-name" style={{ width: '100%', fontSize: '13px' }}> */}
                      {/*        Submission of prices with tax */}
                      {/*      </div> */}
                      {/*    </Checkbox> */}
                      {/*  </div> */}
                      {/* </div> */}
                    </div>
                  </div>
                  <div className="view-rfq-tabs__wrapper">

                    <br />
                    <Tabs
                      activeKey={currentTab}
                      onChange={(key) => {
                        setCurrentTab(key);
                        history.push(`/purchase/rfq/view/${selectedRfq?.rfq_id}?tab=${key}`);
                      }}
                      mode="horizontal"
                      type="card"
                    >
                      <TabPane
                        tab={(
                          <div>
                            RFQ Lines &nbsp;
                            <Badge count={selectedRfq?.rfq_lines?.length} />
                          </div>
                        )}
                        key="/rfq-lines"
                      />
                      <TabPane
                        tab={(
                          <div>
                            Invites Sent &nbsp;
                            <Badge count={selectedRfq?.rfq_participants?.length} />
                          </div>
                        )}
                        key="/invites-sent"
                      />
                      {selectedRfq?.status !== 'DRAFT' && (selectedRfq?.rfq_participants?.length > 0) && selectedRfq?.negotiations_by_rank?.length > 0 && (
                        <TabPane
                          tab={(
                            <div>
                              Participants &nbsp;
                              <Badge count={selectedRfq?.negotiations_by_rank?.length} />
                            </div>
                          )}
                          key="/participants"
                        />
                      )}
                    </Tabs>

                    {/* RFQ Lines */}
                    {currentTab === '/rfq-lines' && (
                      <Fragment>
                        <RfqLines
                          selectedRfq={selectedRfq}
                          rfqLineData={rfqLineData}
                          loading={getRfqByIdLoading || updateRfqParticipantLoading || updateRfqStatusLoading}
                          callback={() => {
                            setCall(!call);
                            setIsCallbackApiCalled(true);
                          }}
                          setRfqLineData={(copyData) => setRfqLineData(copyData)}
                        />
                        {/* Footer */}
                        <div className="view-document__totals-wrapper">
                          <div className="view-document-footer-left">
                            {selectedRfq?.terms_and_condition && (
                              <div className="view-document-tc">
                                <H3Text text="Terms and Conditions" className="view-document-tc-title" />
                                <div className="view-document-tc-body">
                                  <ReactQuill
                                    value={selectedRfq?.terms_and_condition ? selectedRfq?.terms_and_condition : ''}
                                    readOnly
                                    theme="bubble"
                                  />
                                </div>
                              </div>
                            )}

                            {/* <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Attachment
                                label="Internal Attachment(s)"
                                fileList={selectedRfq?.attachments}
                                disableCase={getRfqByIdLoading || updateAttachmentLoading}
                                labelClassName="orgFormLabel"
                                inputClassName=""
                                containerClassName="view__so-attachment"
                                handleFileChange={(value) => handleFileChange(value)}
                                attachmentLength={selectedRfq?.attachments?.length}
                              />
                              <div style={{ marginLeft: '10px' }}>
                                <Attachment
                                  label="Public Attachment(s)"
                                  fileList={selectedRfq?.public_attachments}
                                  disableCase={getRfqByIdLoading || updateAttachmentLoading}
                                  labelClassName="orgFormLabel"
                                  inputClassName=""
                                  containerClassName="view__so-attachment"
                                  handleFileChange={(value) => handleFileChange(value)}
                                  attachmentLength={selectedRfq?.public_attachments?.length}
                                />
                              </div>
                            </div> */}
                          </div>
                        </div>
                      </Fragment>
                    )}

                    {/* Invitees */}
                    {currentTab === '/invites-sent' && (
                      <Fragment>
                        <Invitees
                          data={data?.map((item) => ({
                            ...item,
                            whatsapp_enabled: !!item?.phone_number,
                            email_enabled: !!item?.email,
                          }))}
                          updateData={(copyData) => setData(copyData)}
                          participants={selectedRfq?.rfq_participants}
                          selectedRfq={selectedRfq}
                          isEditing={isEditing}
                          setIsEditing={setIsEditing}
                          addNewRow={addNewRow}
                          handleSubmit={handleSubmit}
                          handleDelete={handleDelete}
                          loading={getRfqByIdLoading || updateRfqParticipantLoading}
                          formSubmitted={formSubmitted}
                          callback={() => setCall(!call)}
                        />
                      </Fragment>
                    )}
                    {/* Participants */}
                    {currentTab === '/participants' && selectedRfq?.status !== 'DRAFT' && (selectedRfq?.rfq_participants?.length > 0) && ((isDataMaskingPolicyEnable && isHideCostPrice) ? <HideComponent /> : (
                      <Fragment>
                        <Participants
                          selectedRfq={selectedRfq}
                          loading={getRfqByIdLoading || updateRfqParticipantLoading || updateRfqStatusLoading}
                          callback={() => {
                            setCall(!call);
                            setIsCallbackApiCalled(true);
                          }}
                        />
                      </Fragment>
                    ))}

                  </div>
                </div>
              </div>
              <div className="ant-col-md-6 ant-col-xs-24">
                <div className={`view-right__wrapper ${!isQuickView ? 'is-page-view' : ''}`}>
                  <div className="rfq__wrapper__approval">
                    {selectedRfq?.negotiations_by_rank?.length > 0 && (
                      <Leaderboard
                        value={selectedRfq?.negotiations_by_rank}
                      />
                    )}

                    {Helpers.getPermission(Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.READ, user) && selectedRfq?.reference_pr?.length > 0 && (
                      <div className="linked-doc__wrapper">
                        <H3Text text="MATERIAL REQUEST" className="linked-doc__title" />
                        {selectedRfq?.reference_pr?.map((item) => (
                          <Link
                            key={item?.purchase_requisition_id}
                            to={`/purchase/purchase-request/view/${item?.purchase_requisition_id}`}
                            target="blank"
                          >
                            <div className="linked-doc__item-header">
                              <CaretRightOutlined />
                              <div className="linked-doc__item-header-id-status__wrapper">
                                <H3Text text={`MR# ${item?.pr_number}`} className="linked-doc__item-header-id"/>
                                <div
                                  className="linked-doc__item-header-status"
                                  style={{ backgroundColor: Helpers.getStatusColor(item?.status)?.color }}
                                >
                                  {Helpers.getStatusColor(item?.status)?.text}
                                </div>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}

                    {Helpers.getPermission(Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ, user) && selectedRfq?.reference_po?.length > 0 && (
                      <div className="linked-doc__wrapper">
                        <H3Text text="PURCHASE ORDER" className="linked-doc__title" />
                        {selectedRfq?.reference_po?.map((item) => (
                          <Link
                            key={item?.po_id}
                            to={`/approval?type=po&id=${item?.po_id}`}
                            target="blank"
                          >
                            <div className="linked-doc__item-header">
                              <CaretRightOutlined />
                              <div className="linked-doc__item-header-id-status__wrapper">
                                <H3Text text={`PO# ${item?.po_number}`} className="linked-doc__item-header-id"/>
                                <div
                                  className="linked-doc__item-header-status"
                                  style={{ backgroundColor: Helpers.getStatusColor(item?.status)?.color }}
                                >
                                  {Helpers.getStatusColor(item?.status)?.text}
                                </div>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>  
              </div>
            </div>
          )
          : (
            //  Loader
            <ViewLoadingSkull isQuickView={isQuickView} fixedMenuBar={fixedMenuBar} />
          )}
      </div>

      <Drawer
        open={showRfqPoDrawer}
        width="1045px"
        mask
        onClose={() => setShowRfqPoDrawer(false)}
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '1000px' }}>
            <H3Text text="Create Purchase Orders" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowRfqPoDrawer(false)} />
          </div>
        </div>

        <RfqPurchaseOrders
          rfqId={selectedRfq?.rfq_id}
          selectedRfq={selectedRfq}
          selection={selectedRfq?.selection}
          allOffers={selectedRfq?.negotiations_by_rank}
          callback={() => {
            setCall(!call);
            setShowRfqPoDrawer(false);
          }}
        />
      </Drawer>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, AttachmentReducers, RfqReducers,
}) => ({
  user: UserReducers.user,
  selectedAttachment: AttachmentReducers.selectedAttachment,
  getAttachmentByIdLoading: AttachmentReducers.getAttachmentByIdLoading,
  updateAttachmentLoading: AttachmentReducers.updateAttachmentLoading,
  getRfqByIdLoading: RfqReducers.getRfqByIdLoading,
  updateRfqStatusLoading: RfqReducers.updateRfqStatusLoading,
  updateRfqConfigLoading: RfqReducers.updateRfqConfigLoading,
  updateRfqParticipantLoading: RfqReducers.updateRfqParticipantLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  getRfqById: (orgId, tenantId, rfqId, callback, dontShowLoading) => dispatch(RfqActions.getRfqById(orgId, tenantId, rfqId, callback, dontShowLoading)),
  updateRfqParticipant: (payload, callback) => dispatch(RfqActions.updateRfqParticipant(payload, callback)),
  updateRfqStatus: (payload, callback) => dispatch(RfqActions.updateRfqStatus(payload, callback)),
  updateRfqConfig: (payload, callback) => dispatch(RfqActions.updateRfqConfig(payload, callback)),
  getAttachmentById: (entityId, entityName, callback) => dispatch(AttachmentActions.getAttachmentById(entityId, entityName, callback)),
  updateAttachment: (payload, callback) => dispatch(AttachmentActions.updateAttachment(payload, callback)),
  downloadDocument: (payload, document) => dispatch(AnalyticsActions.downloadDocument(payload, document)),
});

ViewRfq.propTypes = {
  user: PropTypes.any,
  match: PropTypes.any,
  history: PropTypes.any,
  updateRfqParticipant: PropTypes.func,
  updateRfqParticipantLoading: PropTypes.bool,
  getRfqById: PropTypes.func,
  getRfqByIdLoading: PropTypes.bool,
  updateRfqStatus: PropTypes.func,
  updateRfqStatusLoading: PropTypes.bool,
  updateRfqConfig: PropTypes.func,
  getAttachmentById: PropTypes.func,
  updateAttachment: PropTypes.func,
  updateRfqConfigLoading: PropTypes.bool,
  updateAttachmentLoading: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewRfq));
